// File: src/pages/ActivityLog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Table,
} from "reactstrap";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import swal from "sweetalert";

import { Activity_Log_List } from "../../import/ApiEndPoint/Api";
import { _Get } from "../../import/ApiEndPoint/ApiCalling"; // keep import as-is (even if unused)
import api from "../../import/axiosConfig"; // ✅ use same axios instance as Login.jsx

/* =========================
   Inline CSS (inject once)
   ========================= */
const injectOnce = () => {
  const id = "activity-log-inline-css";
  if (document.getElementById(id)) return;
  const s = document.createElement("style");
  s.id = id;
  s.innerHTML = `
    .al-wrap{ --gap:8px; }
    .al-tbar{ display:flex; gap:var(--gap); flex-wrap:wrap; align-items:center; padding:8px 10px; border:1px solid #e5e7eb; border-radius:14px; background:#f7faf9; }
    .al-sticky{ position:sticky; top:0; background:#fff; z-index:2; }
    .al-pill{ padding:2px 10px; border-radius:9999px; background:#eef2ff; color:#1f2a44; font-weight:600; border:1px solid #dbeafe; }
    .al-table{ font-size:13px; }
    .al-link{ color:#0b5ed7; cursor:pointer; text-decoration:underline; }
    .al-ua{ max-width: 360px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .al-risk-low{ background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; padding:2px 8px; border-radius:9999px; }
    .al-risk-med{ background:#fffbeb; color:#92400e; border:1px solid #fde68a; padding:2px 8px; border-radius:9999px; }
    .al-risk-high{ background:#fef2f2; color:#991b1b; border:1px solid #fecaca; padding:2px 8px; border-radius:9999px; }
    .al-col-list{ columns:2; }
    .al-right{ margin-left:auto; display:flex; gap:8px; align-items:center; }
    .al-muted{ color:#6b7280; font-size:12px; }
  `;
  document.head.appendChild(s);
};

const LS_COLS_KEY = "ActivityLogCols";

const ALL_COLS = [
  { key: "name", label: "Login Name" },
  { key: "email", label: "Login Email" },
  { key: "ip", label: "IP Address" },
  { key: "coords", label: "Long / Latitude" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "loginTime", label: "Login Time" },
  { key: "logoutTime", label: "Logout Time" },
  { key: "duration", label: "Duration" },
  { key: "device", label: "Device / OS" },
  { key: "ua", label: "User Agent" },
  { key: "risk", label: "Risk" },
];

const DEFAULT_VISIBLE = [
  "name",
  "email",
  "ip",
  "coords",
  "city",
  "state",
  "loginTime",
  "logoutTime",
  "duration",
  "risk",
];

/* =========================
   Small utilities
   ========================= */
const fmtDateTime = (ts) => {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString("en-IN", {
      hour12: false,
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return String(ts);
  }
};

const msToDHMS = (ms) => {
  if (!ms || ms < 0) return "";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return (
    (d ? d + "d " : "") +
    [h, m, sec].map((n) => String(n).padStart(2, "0")).join(":")
  );
};

const riskBadge = (r) =>
  r >= 2 ? (
    <span className="al-risk-high">High</span>
  ) : r === 1 ? (
    <span className="al-risk-med">Medium</span>
  ) : (
    <span className="al-risk-low">Low</span>
  );

const openOnMap = (lat, lng) => {
  if (lat == null || lng == null) return;
  window.open(
    `https://www.google.com/maps?q=${lat},${lng}`,
    "_blank",
    "noopener,noreferrer"
  );
};

/* =========================
   Component
   ========================= */
const ActivityLog = () => {
  useEffect(injectOnce, []);

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const fromLS = JSON.parse(localStorage.getItem(LS_COLS_KEY));
      if (Array.isArray(fromLS) && fromLS.length) return fromLS;
    } catch {}
    return DEFAULT_VISIBLE;
  });

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [colModal, setColModal] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshMs, setRefreshMs] = useState(15000);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  // ✅ Simple, reliable fetch using axiosConfig (`api`)
  const fetchLogs = async () => {
    try {
      setLoading(true);

      // This will hit: baseURL + "/activity-logs/list"
      const res = await api.get(Activity_Log_List);

      // Accept shapes: {status, logs}, {logs}, or raw array
      const data =
        (res?.data && Array.isArray(res.data.logs) && res.data.logs) ||
        (Array.isArray(res?.data) && res.data) ||
        [];

      setLogs(Array.isArray(data) ? data : []);
      setLastUpdatedAt(new Date());
    } catch (e) {
      console.error("Failed to load Activity Logs:", e);
      swal("Could not load Activity Logs. Showing empty table.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch once on mount
  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Optional polling — OFF by default
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      fetchLogs();
    }, Math.max(5000, Number(refreshMs) || 15000));
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshMs, fromDate, toDate, search]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromMs = fromDate
      ? new Date(fromDate + "T00:00:00").getTime()
      : -Infinity;
    const toMs = toDate ? new Date(toDate + "T23:59:59").getTime() : Infinity;

    const data = (logs || [])
      .filter((r) => {
        const t = r.loginAt ? new Date(r.loginAt).getTime() : 0;
        if (t < fromMs || t > toMs) return false;
        if (!q) return true;
        return (
          (r.name || "").toLowerCase().includes(q) ||
          (r.email || "").toLowerCase().includes(q) ||
          (r.ip || "").toLowerCase().includes(q) ||
          (r.city || "").toLowerCase().includes(q) ||
          (r.state || "").toLowerCase().includes(q) ||
          (r.device || "").toLowerCase().includes(q) ||
          (r.ua || "").toLowerCase().includes(q)
        );
      })
      .sort(
        (a, b) =>
          new Date(b.loginAt || b.createdAt) -
          new Date(a.loginAt || a.createdAt)
      );
    return data;
  }, [logs, search, fromDate, toDate]);

  const exportXLSX = () => {
    const rows = filtered.map((r) => ({
      "Login Name": r.name,
      "Login Email": r.email,
      "IP Address": r.ip,
      Longitude: r.longitude ?? "",
      Latitude: r.latitude ?? "",
      City: r.city,
      State: r.state,
      "Login Time": fmtDateTime(r.loginAt),
      "Logout Time": fmtDateTime(r.logoutAt),
      Duration: msToDHMS(
        r.durationMs ||
          new Date(r.logoutAt || Date.now()) - new Date(r.loginAt || Date.now())
      ),
      "Device/OS": r.device,
      "User Agent": r.ua,
      Risk: r.risk >= 2 ? "High" : r.risk === 1 ? "Medium" : "Low",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ActivityLog");
    XLSX.writeFile(wb, `activity_log_${fromDate}_to_${toDate}.xlsx`);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "l", unit: "pt", format: "A4" });
    const head = [
      [
        "Login Name",
        "Login Email",
        "IP",
        "Long/Lat",
        "City",
        "State",
        "Login Time",
        "Logout Time",
        "Duration",
        "Device/OS",
        "Risk",
      ],
    ];
    const body = filtered.map((r) => [
      r.name,
      r.email,
      r.ip,
      `${r.longitude ?? ""}, ${r.latitude ?? ""}`,
      r.city,
      r.state,
      fmtDateTime(r.loginAt),
      fmtDateTime(r.logoutAt),
      msToDHMS(
        r.durationMs ||
          new Date(r.logoutAt || Date.now()) - new Date(r.loginAt || Date.now())
      ),
      r.device,
      r.risk >= 2 ? "High" : r.risk === 1 ? "Medium" : "Low",
    ]);
    doc.text(`Activity Log (${fromDate} → ${toDate})`, 40, 32);
    doc.autoTable({
      head,
      body,
      startY: 48,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
    });
    doc.save(`activity_log_${fromDate}_to_${toDate}.pdf`);
  };

  const toggleCol = (k) => {
    setVisibleCols((prev) => {
      const has = prev.includes(k);
      const next = has ? prev.filter((x) => x !== k) : [...prev, k];
      localStorage.setItem(LS_COLS_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div className="al-wrap">
      <Card>
        <div className="al-sticky">
          <div className="al-tbar">
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ width: 160 }}
            />
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ width: 160 }}
            />
            <Input
              placeholder="Search (name/email/IP/city/state/UA)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ maxWidth: 360 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchLogs();
              }}
            />
            <Button color="primary" onClick={fetchLogs}>
              Search / Refresh
            </Button>
            <span className="al-pill">Count: {filtered.length}</span>

            <div className="al-right">
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <Input
                  type="switch"
                  id="autoRefreshSwitch"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  label="Auto-refresh"
                />
                <Input
                  type="number"
                  min={5000}
                  step={1000}
                  style={{ width: 120 }}
                  value={refreshMs}
                  onChange={(e) => setRefreshMs(e.target.value)}
                  disabled={!autoRefresh}
                  title="Interval (ms)"
                />
              </div>

              <Button
                color="secondary"
                outline
                onClick={() => setColModal(true)}
              >
                Columns
              </Button>
              <Button color="success" onClick={exportXLSX}>
                Export XLSX
              </Button>
              <Button color="dark" onClick={exportPDF}>
                Export PDF
              </Button>
            </div>
          </div>
        </div>

        <CardBody>
          <div className="d-flex justify-content-between mb-2">
            <span className="al-muted">
              Showing {filtered.length} of {logs.length} records
            </span>
            <span className="al-muted">
              {lastUpdatedAt
                ? `Last updated: ${fmtDateTime(lastUpdatedAt)}`
                : ""}
            </span>
          </div>

          {loading ? (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: 220 }}
            >
              <Spinner />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table bordered responsive hover className="al-table">
                <thead>
                  <tr>
                    {visibleCols.includes("name") && <th>Login Name</th>}
                    {visibleCols.includes("email") && <th>Login Email</th>}
                    {visibleCols.includes("ip") && <th>IP Address</th>}
                    {visibleCols.includes("coords") && <th>Long / Latitude</th>}
                    {visibleCols.includes("city") && <th>City</th>}
                    {visibleCols.includes("state") && <th>State</th>}
                    {visibleCols.includes("loginTime") && <th>Login Time</th>}
                    {visibleCols.includes("logoutTime") && <th>Logout Time</th>}
                    {visibleCols.includes("duration") && <th>Duration</th>}
                    {visibleCols.includes("device") && <th>Device / OS</th>}
                    {visibleCols.includes("ua") && <th>User Agent</th>}
                    {visibleCols.includes("risk") && <th>Risk</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, idx) => (
                    <tr key={r._id || idx}>
                      {visibleCols.includes("name") && <td>{r.name || "-"}</td>}
                      {visibleCols.includes("email") && (
                        <td>{r.email || "-"}</td>
                      )}
                      {visibleCols.includes("ip") && <td>{r.ip || "-"}</td>}
                      {visibleCols.includes("coords") && (
                        <td>
                          {r.longitude != null && r.latitude != null ? (
                            <span
                              className="al-link"
                              onClick={() => openOnMap(r.latitude, r.longitude)}
                              title="Open in Google Maps"
                            >
                              {`${r.longitude}, ${r.latitude}`}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                      )}
                      {visibleCols.includes("city") && <td>{r.city || "-"}</td>}
                      {visibleCols.includes("state") && (
                        <td>{r.state || "-"}</td>
                      )}
                      {visibleCols.includes("loginTime") && (
                        <td>{fmtDateTime(r.loginAt)}</td>
                      )}
                      {visibleCols.includes("logoutTime") && (
                        <td>{fmtDateTime(r.logoutAt)}</td>
                      )}
                      {visibleCols.includes("duration") && (
                        <td>
                          {msToDHMS(
                            r.durationMs ||
                              new Date(r.logoutAt || Date.now()) -
                                new Date(r.loginAt || Date.now())
                          )}
                        </td>
                      )}
                      {visibleCols.includes("device") && (
                        <td>{r.device || "-"}</td>
                      )}
                      {visibleCols.includes("ua") && (
                        <td className="al-ua" title={r.ua}>
                          {r.ua || "-"}
                        </td>
                      )}
                      {visibleCols.includes("risk") && (
                        <td>{riskBadge(r.risk ?? 0)}</td>
                      )}
                    </tr>
                  ))}
                  {!filtered.length && (
                    <tr>
                      <td colSpan={12} className="text-center text-muted">
                        No activity found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Column chooser */}
      <Modal isOpen={colModal} toggle={() => setColModal(false)}>
        <ModalHeader toggle={() => setColModal(false)}>
          Choose Columns
        </ModalHeader>
        <ModalBody>
          <div className="al-col-list">
            {ALL_COLS.map((c) => (
              <Label
                key={c.key}
                className="d-flex align-items-center"
                style={{ gap: 8 }}
              >
                <Input
                  type="checkbox"
                  checked={visibleCols.includes(c.key)}
                  onChange={() => toggleCol(c.key)}
                />
                {c.label}
              </Label>
            ))}
          </div>
          <div className="d-flex justify-content-end" style={{ gap: 8 }}>
            <Button
              color="secondary"
              onClick={() => {
                localStorage.setItem(
                  LS_COLS_KEY,
                  JSON.stringify(DEFAULT_VISIBLE)
                );
                setVisibleCols(DEFAULT_VISIBLE);
              }}
            >
              Reset
            </Button>
            <Button color="primary" onClick={() => setColModal(false)}>
              Done
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ActivityLog;
